/**
 * Local development entry point.
 *
 * In production, ModuleApp is loaded as a remote module by the xplatform shell.
 * Standalone, this file renders it directly. Under the DEV-ONLY preview server
 * (`vite --mode preview`) it additionally mounts a fake auth hook (via the vite
 * alias) and the preview chrome (sign-in chooser + neutral pill / Log out
 * toolbar). There are TWO dev preview sub-modes, both gated by `__PREVIEW__`:
 *
 *   npm run dev:preview            VITE_FAKE_API=true  -> in-memory fake API
 *   npm run dev:preview:real-api   VITE_FAKE_API unset -> the REAL ReviewApi
 *                                  built from VITE_API_BASE_URL, so the real UI
 *                                  runs against the real local review runner
 *                                  (app/generation_api/scripts/
 *                                  run_local_review_api.py). The fake sign-in
 *                                  dropdown stays, but the chosen user now
 *                                  sends a `dev:<userId>:<perms>` bearer token
 *                                  (see dev/fakeAuth.ts) and the fake API is
 *                                  NOT loaded.
 *
 * The `__PREVIEW__` guard is a compile-time constant: a real `vite build` sets
 * it to `false`, so the `dev/` imports below are dead code that tree-shakes out
 * and never reaches a shipped bundle.
 */
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@config/i18n";
import ModuleApp, { type ModuleAppProps } from "@src/ModuleApp";

async function bootstrap() {
  const root = createRoot(document.getElementById("root")!);

  if (__PREVIEW__) {
    // Dev-only: load the preview chrome (sign-in chooser + toolbar). These
    // modules live under dev/ and are referenced ONLY here behind __PREVIEW__,
    // so they are excluded from every build.
    const { PreviewChrome } = await import("../dev/PreviewChrome");

    // The fake in-memory API is loaded ONLY in the fake-API sub-mode. In the
    // real-api sub-mode `api` stays undefined and ModuleApp builds the real
    // client from VITE_API_BASE_URL (authenticated by the dev token).
    let api: ModuleAppProps["api"] | undefined;
    if (import.meta.env.VITE_FAKE_API === "true") {
      const { FakeReviewApi } = await import("../dev/fakeApi/FakeReviewApi");
      api = new FakeReviewApi() as unknown as ModuleAppProps["api"];
    }

    root.render(
      <BrowserRouter>
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <PreviewChrome>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ModuleApp api={api} />
            </div>
          </PreviewChrome>
        </div>
      </BrowserRouter>,
    );
    return;
  }

  root.render(
    <BrowserRouter>
      <ModuleApp />
    </BrowserRouter>,
  );
}

void bootstrap();
